import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import {
  faBolt,
  faBuilding,
  faCalendar,
  faDoorOpen,
  faFileShield,
  faFileSignature,
  faHouse,
  faKey,
  faListCheck,
  faMagnifyingGlass,
  faPhoneVolume,
  faPlus,
  faShieldHalved,
  faToolbox,
  faTruck,
  faWrench
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../context/AuthContext';
import { useMaintenance } from '../../queries/maintenance';
import { usePropertyQuery } from '../../queries/properties';
import { formatMoney, layoutOf } from '../Properties/constants';
import { stageIndex } from '../../utils/contacts';
import { Btn, Chip, cx } from '../Todos/ui';
import { Kv, Lrow, Muted, Sec } from './parts';

const day = (d) => (d ? dayjs(d).format('MMM D, YYYY') : '');
const when = (d) => (d ? dayjs(d).format('MMM D · h:mm A') : '');

function budgetOf(intake = {}) {
  const lo = intake.budgetLow;
  const hi = intake.budgetHigh;
  if (!lo && !hi) return 'Not given';
  return lo && hi && lo !== hi ? `${lo} to ${hi}` : lo || hi;
}

function applicationOf(stage) {
  const i = stageIndex(stage);
  if (stage === 'Not Eligible') return ['Not eligible', 'bad'];
  if (i < stageIndex('Application Submitted')) return ['Not sent yet', 'neutral'];
  if (i < stageIndex('Fully Qualified')) return ['Submitted, in screening', 'warn'];
  return ['Complete', 'ok'];
}

function ProspectOverview({ client, history, onSchedule }) {
  const { can } = useAuth();
  const intake = client.intake || {};
  const qual = can('view_prospect_qualification') ? client.qualification || {} : {};
  const app = applicationOf(client.pipelineStage);
  const screening = [
    intake.moveDate || intake.timeframe ? ['Move-in', intake.moveDate || intake.timeframe] : null,
    qual.income ? ['Monthly household income', qual.income] : null,
    intake.occupants ? ['Who will live there', `${intake.occupants} people`] : null,
    intake.employer ? ['Employer', intake.employer] : null,
    intake.tourAvailability ? ['Tour availability', intake.tourAvailability] : null,
    intake.pets ? ['Pets', intake.pets] : null,
    qual.evictions ? ['Evictions', qual.evictions] : null,
    qual.background ? ['Background', qual.background] : null
  ].filter(Boolean);
  const calls = history.filter((h) => h.kind === 'call');
  return (
    <>
      <Sec title="Looking for" icon={faMagnifyingGlass}>
        <Kv
          rows={[
            ['Desired unit', client.interestedProperty || 'Not given'],
            ['Budget', budgetOf(intake)],
            intake.bedrooms ? ['Bedrooms', intake.bedrooms] : null,
            ['Move-in', intake.moveDate || intake.timeframe || 'Not given']
          ]}
        />
      </Sec>
      <Sec title="Application" icon={faFileSignature}>
        <div className="m-row wrap">
          <Chip fam={app[1]}>{app[0]}</Chip>
          <Chip fam="info" icon={faKey}>
            {client.pipelineStage || 'Lead'}
          </Chip>
        </div>
      </Sec>
      <Sec title="Screening questions" icon={faListCheck}>
        {screening.length ? <Kv rows={screening} /> : <Muted>Asked when the application is sent.</Muted>}
      </Sec>
      <Sec
        title="Tours"
        icon={faKey}
        right={
          <Btn sm icon={faPlus} onClick={() => onSchedule('tour')}>
            Schedule tour
          </Btn>
        }
      >
        {client.tour?.start ? (
          <Lrow icon={faCalendar} title="Tour" sub={`${when(client.tour.start)}${client.interestedProperty ? ` · ${client.interestedProperty}` : ''}`} />
        ) : (
          <Muted>No tours yet.</Muted>
        )}
      </Sec>
      <Sec title="Call recordings" icon={faPhoneVolume}>
        {calls.length ? (
          calls.map((h) => <Lrow key={`${h.at}-${h.label}`} icon={faPhoneVolume} title={`${h.label} · ${when(h.at)}`} sub={h.text} />)
        ) : (
          <Muted>No calls yet.</Muted>
        )}
      </Sec>
    </>
  );
}

function TenantOverview({ client, onSchedule }) {
  const unitName = client.unit?.name || '';
  const { data: unit } = usePropertyQuery(client.unit?._id);
  const { data: maint } = useMaintenance({}, Boolean(unitName));
  const tickets = (maint?.tickets || []).filter((t) => t.property.toLowerCase() === unitName.toLowerCase());
  const rent = unit?.listing?.rent;
  return (
    <>
      <Sec title="Home and lease" icon={faHouse}>
        <Kv
          rows={[
            ['Property or unit', unitName || 'Not linked'],
            ['Lease', client.lease || 'Not on file'],
            ['Monthly rent', rent ? formatMoney(rent) : '--'],
            ['Status', client.tenantStatus || 'Active']
          ]}
        />
      </Sec>
      <Sec
        title="Maintenance requests"
        icon={faWrench}
        right={
          <Btn sm icon={faPlus} onClick={() => onSchedule('maint')}>
            Schedule maintenance
          </Btn>
        }
      >
        {tickets.length ? (
          tickets.map((t) => (
            <Lrow
              key={t.id}
              icon={faWrench}
              title={`${t.number ? `${t.number} · ` : ''}${t.issue}`}
              sub={[t.status, t.assigned].filter(Boolean).join(' · ')}
              right={<Chip fam={t.tierKey === 'emergency' ? 'bad' : 'neutral'}>{t.tier}</Chip>}
            />
          ))
        ) : (
          <Muted>No requests.</Muted>
        )}
      </Sec>
      {client.tenantStatus === 'Active - Notice Given' && (
        <Sec title="Move-out" icon={faTruck}>
          <div className="sm">Notice given. Plan the move-out walkthrough with a leasing agent.</div>
        </Sec>
      )}
    </>
  );
}

function OwnerOverview({ owner, onDocs }) {
  const navigate = useNavigate();
  const props = owner.properties || [];
  const vacant = props.filter((p) => !p.tenantName);
  const openUnit = (p) => navigate('/property', { state: { unitId: p._id } });
  const agreements = (owner.files || []).filter((f) => f.type === 'Agreement Signed');
  return (
    <>
      <Sec title="Properties" icon={faBuilding}>
        {props.length ? (
          props.map((p) => (
            <Lrow
              key={p._id}
              icon={faHouse}
              title={p.name}
              sub={[layoutOf(p), p.rent ? `${formatMoney(p.rent)} a month` : null].filter(Boolean).join(' · ')}
              right={p.tenantName ? <Chip fam="ok">Occupied</Chip> : <Chip fam="warn">Vacant</Chip>}
              onClick={() => openUnit(p)}
            />
          ))
        ) : (
          <Muted>No properties linked.</Muted>
        )}
      </Sec>
      <Sec title="Vacancies" icon={faDoorOpen}>
        {vacant.length ? (
          vacant.map((p) => <Lrow key={p._id} icon={faDoorOpen} title={p.name} onClick={() => openUnit(p)} />)
        ) : (
          <Muted>No vacant units right now.</Muted>
        )}
      </Sec>
      <Sec title="Management agreement" icon={faFileSignature}>
        {agreements.length ? (
          agreements.map((f) => (
            <Lrow key={f._id} icon={faFileSignature} title={f.name} sub={`Added ${day(f.addedAt)}`} right={<Chip fam="ok">On file</Chip>} onClick={onDocs} />
          ))
        ) : (
          <Lrow icon={faFileSignature} title="Management agreement" sub="Not on file yet" right={<Chip fam="warn">Missing</Chip>} onClick={onDocs} />
        )}
      </Sec>
    </>
  );
}

function VendorOverview({ vendor, openTickets, onSchedule }) {
  const exp = vendor.insuranceExpiresAt;
  const expired = exp && dayjs(exp).isBefore(dayjs());
  return (
    <>
      <Sec title="Company" icon={faToolbox}>
        <Kv
          rows={[
            ['Company', vendor.company],
            ['Contact', vendor.contact || '--'],
            ['Services', vendor.trade || '--'],
            ['Service area', vendor.coverage || '--'],
            ['Rates', vendor.rate || '--']
          ]}
        />
      </Sec>
      <Sec title="Insurance" icon={faShieldHalved}>
        <Lrow
          icon={faFileShield}
          title="Certificate of insurance"
          sub={exp ? `Expires ${day(exp)}` : 'Not on file yet'}
          right={!exp ? <Chip fam="warn">Missing</Chip> : expired ? <Chip fam="bad">Expired</Chip> : <Chip fam="ok">Current</Chip>}
        />
      </Sec>
      <Sec
        title="Open work orders"
        icon={faWrench}
        right={
          <Btn sm icon={faPlus} onClick={() => onSchedule('maint')}>
            Schedule maintenance
          </Btn>
        }
      >
        {openTickets.length ? (
          openTickets.map((t) => (
            <Lrow
              key={t._id}
              icon={faWrench}
              title={`${t.ticketNumber ? `${t.ticketNumber} · ` : ''}${t.issue}`}
              sub={[t.propertyUnit, t.status].filter(Boolean).join(' · ')}
            />
          ))
        ) : (
          <Muted>No open work orders.</Muted>
        )}
      </Sec>
    </>
  );
}

export default function Overview({ c, data, actions, phone, onSchedule, onDocs }) {
  const navigate = useNavigate();
  let body = null;
  if (c.kind === 'client' && c.type === 'tenant') body = <TenantOverview client={data.client} onSchedule={onSchedule} />;
  else if (c.kind === 'client') body = <ProspectOverview client={data.client} history={data.history || []} onSchedule={onSchedule} />;
  else if (c.kind === 'owner') body = <OwnerOverview owner={data.owner} onDocs={onDocs} />;
  else body = <VendorOverview vendor={data.vendor} openTickets={data.openTickets || []} onSchedule={onSchedule} />;
  return (
    <div className={cx('cp-grid', phone && 'ph')}>
      {body}
      {actions.length > 0 && (
        <Sec title="Open in Agent Actions" icon={faListCheck}>
          {actions.map((x) => (
            <Lrow
              key={x._id}
              icon={faBolt}
              title={x.title}
              sub={x.why ? `${x.why.slice(0, 90)}${x.why.length > 90 ? '...' : ''}` : ''}
              onClick={() => navigate('/todos', { state: { openActionId: x._id } })}
            />
          ))}
        </Sec>
      )}
    </div>
  );
}
